import { useEffect, useRef, useCallback } from 'react'

interface UseSoloPilotNudgeOptions {
  timeout?: number
  events?: string[]
  onIdle: () => void
  onActivity?: () => void
}

const DEFAULT_EVENTS = ['mousemove', 'keydown', 'scroll', 'touchstart']
const DEFAULT_TIMEOUT = 7000 // 7 seconds

export const useSoloPilotNudge = ({
  timeout = DEFAULT_TIMEOUT,
  events = DEFAULT_EVENTS,
  onIdle,
  onActivity,
}: UseSoloPilotNudgeOptions) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isIdleRef = useRef(false)

  const resetTimer = useCallback(() => {
    // Clear existing timer
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Reset idle state
    isIdleRef.current = false

    // Set new timer
    timeoutRef.current = setTimeout(() => {
      if (!isIdleRef.current) {
        isIdleRef.current = true
        onIdle()
      }
    }, timeout)
  }, [onIdle, timeout])

  const handleActivity = useCallback(() => {
    onActivity?.()
    resetTimer()
  }, [resetTimer, onActivity])

  useEffect(() => {
    // Add event listeners for all activity events
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true)
    })

    // Start the initial timer
    resetTimer()

    // Cleanup function
    return () => {
      // Remove event listeners
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true)
      })

      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [events, handleActivity, resetTimer])

  return {
    resetTimer,
    isIdle: isIdleRef.current,
  }
}
